import React from "react";
import styled from "styled-components";
import {
  Modal,
  DropdownSingle as DropdownSingleRaw,
  UI_CAPTION,
  ButtonTertiaryCompact,
} from "oolib";
import { ArrowArcLeft, DownloadSimple } from "@phosphor-icons/react";
import { t, themeNames, ThemeName } from "../theme";

const DropdownSingle = DropdownSingleRaw as React.FC<any>;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

type Props = {
  layout: "A" | "B";
  setLayout: (v: "A" | "B") => void;
  theme: ThemeName;
  setTheme: (v: ThemeName) => void;
  fieldsFilter: "all" | "richtext";
  setFieldsFilter: (v: "all" | "richtext") => void;
  distributionPlacement: "sidebar" | "panel";
  setDistributionPlacement: (v: "sidebar" | "panel") => void;
  /** Download the page's annotations as CSV (text/gist/signals/topics/actor/
   *  customer-voice). Pending = waiting on the lazily-fetched doc summary
   *  (needed for the Customer Voice column) before the file generates. */
  onDownloadAnnotations: () => void;
  downloadPending?: boolean;
  onClose: () => void;
};

export const ViewControlsModal: React.FC<Props> = ({
  layout,
  setLayout,
  theme,
  setTheme,
  fieldsFilter,
  setFieldsFilter,
  distributionPlacement,
  setDistributionPlacement,
  onDownloadAnnotations,
  downloadPending = false,
  onClose,
}) => {
  // useLocalStorage doesn't sync across components, so set the key the parent
  // (SM_PublishedPage) reads and reload — it renders V2 on next load.
  const switchToOldUi = () => {
    window.localStorage.setItem("dev_published_page_version", JSON.stringify("v2"));
    window.location.reload();
  };

  // TEMP / quick-and-dirty: which overlapping-annotation picker UI to show.
  // Self-contained here — written to localStorage, read live by AnnoPlugin the
  // next time the picker opens (no parent wiring). Remove once we pick one UI.
  const OVERLAP_PICKER_LS_KEY = "anno_overlap_picker_variant";
  const [overlapPickerVariant, setOverlapPickerVariant] = React.useState<string>(
    () => (window.localStorage.getItem(OVERLAP_PICKER_LS_KEY) === "list" ? "list" : "badges"),
  );
  const handleSetOverlapPickerVariant = (v: string) => {
    window.localStorage.setItem(OVERLAP_PICKER_LS_KEY, v);
    setOverlapPickerVariant(v);
  };

  return (
    <Modal title="View settings" desktopWidth="360px" fitToContentHeight onClose={onClose}>
      <Content>
        <Row>
          <UI_CAPTION style={{ color: t("grey60") }}>Layout</UI_CAPTION>
          <DropdownSingle
            id="vc-layout"
            saveValueAsString
            optionsAnchor="right"
            options={[
              { display: "Layout A", value: "A" },
              { display: "Layout B", value: "B" },
            ]}
            value={layout}
            onChange={(_id: string, v: string) => setLayout(v as "A" | "B")}
          />
        </Row>

        <Row>
          <UI_CAPTION style={{ color: t("grey60") }}>Fields</UI_CAPTION>
          <DropdownSingle
            id="vc-fields"
            saveValueAsString
            optionsAnchor="right"
            options={[
              { display: "All Fields", value: "all" },
              { display: "Richtext Only Fields", value: "richtext" },
            ]}
            value={fieldsFilter}
            onChange={(_id: string, v: string) => setFieldsFilter(v as "all" | "richtext")}
          />
        </Row>

        <Row>
          <UI_CAPTION style={{ color: t("grey60") }}>Distribution</UI_CAPTION>
          <DropdownSingle
            id="vc-distribution-placement"
            saveValueAsString
            optionsAnchor="right"
            options={[
              { display: "Sidebar", value: "sidebar" },
              { display: "Summary panel", value: "panel" },
            ]}
            value={distributionPlacement}
            onChange={(_id: string, v: string) => setDistributionPlacement(v as "sidebar" | "panel")}
          />
        </Row>

        <Row>
          <UI_CAPTION style={{ color: t("grey60") }}>Overlap picker</UI_CAPTION>
          <DropdownSingle
            id="vc-overlap-picker"
            saveValueAsString
            optionsAnchor="right"
            options={[
              { display: "Badges", value: "badges" },
              { display: "List", value: "list" },
            ]}
            value={overlapPickerVariant}
            onChange={(_id: string, v: string) => handleSetOverlapPickerVariant(v)}
          />
        </Row>

        <Row>
          <UI_CAPTION style={{ color: t("grey60") }}>Theme</UI_CAPTION>
          <DropdownSingle
            id="vc-theme"
            saveValueAsString
            optionsAnchor="right"
            options={themeNames.map((name) => ({ display: name, value: name }))}
            value={theme}
            onChange={(_id: string, v: string) => setTheme(v as ThemeName)}
          />
        </Row>

        <Row>
          <UI_CAPTION style={{ color: t("grey60") }}>Annotations</UI_CAPTION>
          <ButtonTertiaryCompact onClick={onDownloadAnnotations} disabled={downloadPending}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <DownloadSimple size={14} weight="bold" />
              {downloadPending ? "Preparing CSV…" : "Download CSV"}
            </span>
          </ButtonTertiaryCompact>
        </Row>

        <Row>
          <UI_CAPTION style={{ color: t("grey60") }}>Interface</UI_CAPTION>
          <ButtonTertiaryCompact onClick={switchToOldUi}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <ArrowArcLeft size={14} weight="bold" /> Switch to old UI
            </span>
          </ButtonTertiaryCompact>
        </Row>
      </Content>
    </Modal>
  );
};

export default ViewControlsModal;
